import { useState, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Button from '@mui/material/Button'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

const inputSx = {
  backgroundColor: 'background.paper',
  border: 0,
  p: 1,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'divider',
  borderRadius: '6px',
  fontSize: '0.85em',
  '&:focus': { borderColor: 'white', outline: 'none' },
}

const labelSx = {
  fontSize: '0.78em',
  color: 'text.secondary',
  mb: 0.5,
  fontWeight: 600,
}

export const usePresetSide = (presets) => {
  const [presetKey, setPresetKey] = useState('default')
  const [minS, setMinS] = useState(15)
  const [maxS, setMaxS] = useState(30)
  const [prompt, setPrompt] = useState('')
  const [originalPrompt, setOriginalPrompt] = useState('')
  const [schemaDescs, setSchemaDescs] = useState({})
  const [originalSchemaDescs, setOriginalSchemaDescs] = useState({})

  useEffect(() => {
    if (!presets?.length) return
    const preset = presets.find((p) => p.key === 'default') || presets[0]
    setPresetKey(preset.key)
    setPrompt(preset.prompt_template)
    setOriginalPrompt(preset.prompt_template)
    setMinS(preset.default_min_s)
    setMaxS(preset.default_max_s)
    setSchemaDescs({ ...preset.schema_descriptions })
    setOriginalSchemaDescs({ ...preset.schema_descriptions })
  }, [presets])

  const selectPreset = useCallback(
    (key) => {
      const preset = presets?.find((p) => p.key === key)
      if (!preset) return
      setPresetKey(key)
      setPrompt(preset.prompt_template)
      setOriginalPrompt(preset.prompt_template)
      setMinS(preset.default_min_s)
      setMaxS(preset.default_max_s)
      setSchemaDescs({ ...preset.schema_descriptions })
      setOriginalSchemaDescs({ ...preset.schema_descriptions })
    },
    [presets]
  )

  const resetPrompt = useCallback(
    () => setPrompt(originalPrompt),
    [originalPrompt]
  )
  const resetSchema = useCallback(
    () => setSchemaDescs({ ...originalSchemaDescs }),
    [originalSchemaDescs]
  )
  const setSchemaDesc = useCallback((key, value) => {
    setSchemaDescs((prev) => ({ ...prev, [key]: value }))
  }, [])

  const promptModified = prompt !== originalPrompt
  const schemaModified = Object.keys(schemaDescs).some(
    (k) => schemaDescs[k] !== originalSchemaDescs[k]
  )

  const getApiParams = useCallback(() => {
    const params = { preset: presetKey, minS, maxS }
    if (promptModified) params.systemPromptOverride = prompt
    if (schemaModified) {
      const overrides = {}
      for (const [k, v] of Object.entries(schemaDescs)) {
        if (v !== originalSchemaDescs[k]) overrides[k] = v
      }
      params.schemaDescriptionOverrides = overrides
    }
    return params
  }, [
    presetKey,
    minS,
    maxS,
    prompt,
    schemaDescs,
    originalSchemaDescs,
    promptModified,
    schemaModified,
  ])

  return {
    presetKey,
    selectPreset,
    minS,
    setMinS,
    maxS,
    setMaxS,
    prompt,
    setPrompt,
    originalPrompt,
    resetPrompt,
    promptModified,
    schemaDescs,
    setSchemaDesc,
    originalSchemaDescs,
    resetSchema,
    schemaModified,
    getApiParams,
  }
}

export const SchemaDescEditor = ({
  schemaDescs,
  originalSchemaDescs,
  onSchemaDescChange,
  onResetSchema,
  compact,
}) => {
  const [schemaOpen, setSchemaOpen] = useState(false)

  const schemaModified =
    schemaDescs &&
    originalSchemaDescs &&
    Object.keys(schemaDescs).some(
      (k) => schemaDescs[k] !== originalSchemaDescs[k]
    )

  if (!schemaDescs) return null

  return (
    <Box sx={{ mt: compact ? 1 : 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setSchemaOpen((v) => !v)}
      >
        <Box
          sx={{
            fontSize: '0.78em',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          Schema Descriptions
        </Box>
        {schemaOpen ? (
          <ExpandLessIcon
            sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }}
          />
        ) : (
          <ExpandMoreIcon
            sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }}
          />
        )}
        {schemaModified && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onResetSchema()
            }}
            sx={{ ml: 1, fontSize: '0.68em', textTransform: 'none' }}
          >
            Reset
          </Button>
        )}
      </Box>
      <Collapse in={schemaOpen}>
        <Box sx={{ mt: 1 }}>
          {Object.keys(schemaDescs).map((key) => {
            if (schemaDescs[key] === undefined) return null
            return (
              <Box key={key} sx={{ mb: 1 }}>
                <Box
                  sx={{
                    fontSize: '0.72em',
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    mb: 0.3,
                  }}
                >
                  {key}
                </Box>
                <Box
                  sx={{
                    '& input': {
                      ...inputSx,
                      width: '100%',
                      fontSize: '0.8em',
                    },
                  }}
                >
                  <input
                    value={schemaDescs[key]}
                    onChange={(e) => onSchemaDescChange(key, e.target.value)}
                  />
                </Box>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

export const PresetSelector = ({
  presets,
  presetKey,
  onPresetChange,
  minS,
  maxS,
  onMinChange,
  onMaxChange,
  compact,
}) => {
  if (!presets || presets.length === 0) return null

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Box sx={labelSx}>Preset</Box>
          <Box
            sx={{
              '& select': {
                ...inputSx,
                width: compact ? 160 : 200,
                color: 'text.primary',
                cursor: 'pointer',
              },
            }}
          >
            <select
              value={presetKey}
              onChange={(e) => onPresetChange(e.target.value)}
            >
              {presets.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </Box>
        </Box>

        <Box>
          <Box sx={labelSx}>Min (s)</Box>
          <Box sx={{ '& input': inputSx }}>
            <input
              type="number"
              min={1}
              value={minS}
              onChange={(e) => onMinChange(parseInt(e.target.value) || 1)}
              style={{ width: 60, textAlign: 'center' }}
            />
          </Box>
        </Box>
        <Box>
          <Box sx={labelSx}>Max (s)</Box>
          <Box sx={{ '& input': inputSx }}>
            <input
              type="number"
              min={1}
              value={maxS}
              onChange={(e) => onMaxChange(parseInt(e.target.value) || 1)}
              style={{ width: 60, textAlign: 'center' }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const PresetConfig = ({
  presets,
  presetKey,
  onPresetChange,
  minS,
  maxS,
  onMinChange,
  onMaxChange,
  schemaDescs,
  originalSchemaDescs,
  onSchemaDescChange,
  onResetSchema,
  compact,
}) => {
  if (!presets || presets.length === 0) return null

  return (
    <Box>
      <PresetSelector
        presets={presets}
        presetKey={presetKey}
        onPresetChange={onPresetChange}
        minS={minS}
        maxS={maxS}
        onMinChange={onMinChange}
        onMaxChange={onMaxChange}
        compact={compact}
      />
      <SchemaDescEditor
        schemaDescs={schemaDescs}
        originalSchemaDescs={originalSchemaDescs}
        onSchemaDescChange={onSchemaDescChange}
        onResetSchema={onResetSchema}
        compact={compact}
      />
    </Box>
  )
}

export default PresetConfig
